import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { hasMoney } from "../api/client";
import type { AdminReferralRow, BookScope } from "../api/types";
import { Counter } from "../components/Counter";
import { ErrorNotice, Loading } from "../components/Notice";
import { ChevronRightIcon } from "../components/icons";
import { PageHead } from "../components/PageHead";
import { BoolChip, KycChip, RoleChip } from "../components/StatusChip";
import { formatCount, formatDate, formatUsd, orDash } from "../format";
import { useAsync, useCompact, useDebounced } from "../hooks";
import { readBook, saveBook } from "../lib/book-snapshot";
import { SECTIONS } from "../sections";
import { useSession } from "../session/SessionContext";

function queryKey(search: string, scope: BookScope) {
  return `${scope}\u0000${search}`;
}

export function Referrals() {
  const { api, staff } = useSession();
  const navigate = useNavigate();
  const { key: entry } = useLocation();
  const isAdmin = staff?.role === "admin";
  const compact = useCompact();
  const staffDid = staff?.privyDid ?? null;

  const [restored] = useState(() => readBook({ entry, api, staffDid }));

  // Admin-only, and one piece of state for both requests below: counters and
  // table must always describe the same population. An RM's book is
  // attribution-bound already, so the scope stays at its default for them,
  // and the default is sent as an absent param, not as "all".
  const [scope, setScope] = useState<BookScope>(restored?.scope ?? "all");
  const scopeParam = isAdmin && scope !== "all" ? { scope } : {};
  const scopeKey = isAdmin ? scope : "all";

  const metrics = useAsync(() => api.metrics(scopeParam), [api, scopeKey]);

  const [search, setSearch] = useState(restored?.search ?? "");
  const debouncedSearch = useDebounced(search.trim(), 300);
  const [rows, setRows] = useState<AdminReferralRow[] | null>(restored?.rows ?? null);
  // Separate from `rows === null`, which now means only "nothing has ever
  // loaded". A refresh keeps the page it is replacing on screen.
  const [refreshing, setRefreshing] = useState(restored === null);
  const [nextCursor, setNextCursor] = useState<string | undefined>(restored?.nextCursor);
  const [error, setError] = useState<unknown>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  // One counter invalidates both requests. The first-page effect bumps it on
  // the way out, so a `loadMore` still in flight when the user types or
  // switches scope is dropped on arrival instead of appending rows from the
  // old query and installing a cursor that belongs to it.
  const generation = useRef(0);

  // The query the restored rows answer. The first-page effect skips a fetch
  // for exactly that query, every time it runs for it (StrictMode runs it
  // twice), and forgets it for good once the query moves on.
  const restoredKey = useRef(restored ? queryKey(restored.search.trim(), restored.scope) : null);

  useEffect(() => {
    const bump = () => {
      generation.current += 1;
    };
    if (restoredKey.current === queryKey(debouncedSearch, scopeKey)) return bump;
    restoredKey.current = null;
    const current = generation.current;
    // The rows stay. A superseded page is already dropped on arrival by the
    // generation guard, so clearing them bought no correctness and cost the
    // reader their place: the panel collapsed to a spinner and the whole page
    // jumped, on every keystroke.
    setRefreshing(true);
    setError(null);
    setNextCursor(undefined);
    api
      .referrals({
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
        ...scopeParam,
      })
      .then((page) => {
        if (current !== generation.current) return;
        setRows(page.items);
        setNextCursor(page.nextCursor);
        setRefreshing(false);
      })
      .catch((err: unknown) => {
        if (current !== generation.current) return;
        setError(err);
        setRefreshing(false);
      });
    return bump;
  }, [api, debouncedSearch, scopeKey]);

  // What the snapshot is taken from on the way out, read at unmount.
  const latest = useRef({ entry, api, staffDid, search, scope, rows, nextCursor, error });
  latest.current = { entry, api, staffDid, search, scope, rows, nextCursor, error };

  // Tracked as it happens rather than read on the way out: by the time this
  // screen's cleanup runs, the member page has replaced the document and the
  // window's scroll has already been clamped to it.
  const scrollY = useRef(window.scrollY);
  useEffect(() => {
    const onScroll = () => {
      scrollY.current = window.scrollY;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(
    () => () => {
      const { rows: leftRows, error: leftError, ...rest } = latest.current;
      saveBook(
        leftRows && !leftError ? { ...rest, rows: leftRows, scrollY: scrollY.current } : null,
      );
    },
    [],
  );

  // Back to the row that was opened. Layout, not a plain effect, so the list
  // is never painted at the top first.
  useLayoutEffect(() => {
    if (restored) window.scrollTo(0, restored.scrollY);
  }, [restored]);

  const loadMore = useCallback(async () => {
    if (!nextCursor || loadingMore) return;
    const current = generation.current;
    setLoadingMore(true);
    try {
      const page = await api.referrals({
        cursor: nextCursor,
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
        ...scopeParam,
      });
      if (current !== generation.current) return;
      setRows((prev) => [...(prev ?? []), ...page.items]);
      setNextCursor(page.nextCursor);
    } catch (err) {
      if (current === generation.current) setError(err);
    } finally {
      // Unconditional: this is the button's own state, and leaving it stuck on
      // a superseded request would disable "Load more" for the new search.
      setLoadingMore(false);
    }
  }, [api, nextCursor, loadingMore, debouncedSearch, scopeKey]);

  const funnel = metrics.data?.funnel;
  const money = metrics.data && hasMoney(metrics.data) ? metrics.data.money : null;
  const ownCode = staff?.referralCode?.code ?? null;

  return (
    <div className="page">
      <PageHead
        title={SECTIONS.referrals.label}
        icon={<SECTIONS.referrals.Icon />}
        aside={isAdmin ? <ScopePicker value={scope} onChange={setScope} /> : null}
      >
        <p className="subtle">
          {isAdmin ? SCOPE_BLURB[scope] : SCOPE_BLURB.mine}
          {ownCode ? ` Your code is ${ownCode}.` : ""}
        </p>
      </PageHead>

      <ErrorNotice error={metrics.error} />

      {/*
        One object, two bands: the same population as the table below, staff
        rows included, counted first in people and then in dollars.

        The money band exists only when the API sent the `money` key. It is
        omitted server-side for RMs, so its absence is authorization, not a
        loading state, and there is nothing to show in its place.
      */}
      <div className="scoreboard">
        <div className="scoreboard-band">
          <Counter
            label="Signed up"
            value={funnel ? formatCount(funnel.signedUp) : "-"}
          />
          <Counter
            label="Invite redeemed"
            value={funnel ? formatCount(funnel.inviteRedeemed) : "-"}
          />
          <Counter
            label="KYC passed"
            value={funnel ? formatCount(funnel.kycPassed) : "-"}
          />
          <Counter
            label="Card issued"
            value={funnel ? formatCount(funnel.cardIssued) : "-"}
          />
        </div>

        {/* Held open with dashes while an admin's metrics load, as the counts
            above it are: arriving late, it pushed the book down under the
            reader, and undid the scroll a return from a member restores. */}
        {money || (isAdmin && metrics.loading) ? (
          <div className="scoreboard-foot">
            <MoneyFact label="Card spend" value={money ? formatUsd(money.cardSpendUsd) : "-"} />
            <MoneyFact
              label="Transferred to card"
              value={money ? formatUsd(money.transferredToCardUsd) : "-"}
            />
          </div>
        ) : null}
      </div>

      <div className="panel">
        <div className="panel-head panel-head-search">
          <h2>Referral book</h2>
          <input
            className="search"
            type="search"
            value={search}
            placeholder="Search by email, code, name or account ID"
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>

        <ReferralsBody
          rows={rows}
          refreshing={refreshing}
          error={error}
          search={debouncedSearch}
          ownCode={ownCode}
          compact={compact}
          onOpen={(row) => navigate(`/users/${row.userId}`, { state: { row } })}
        />

        {nextCursor ? (
          <div className="panel-foot">
            <button type="button" disabled={loadingMore} onClick={() => void loadMore()}>
              {loadingMore ? "Loading..." : "Load more"}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * One money total, as a line of text rather than a counter tile. Same reason
 * the band it sits in is shaped differently: an amount the API derives is not
 * the same kind of fact as a count of people, and drawing it at the same size
 * made four counts and two amounts argue for the same attention.
 */
function MoneyFact({ label, value }: { label: string; value: string }) {
  return (
    <span className="money-fact">
      <span className="money-fact-label">{label}</span>
      <span className="money-fact-value">{value}</span>
    </span>
  );
}

/**
 * The book scope, in the order the picker shows it. `all` is app-wide: users
 * who predate the invite gate have no attributing code, and an admin's "all"
 * has to include them. `mine` is the one view an RM has, so its blurb doubles
 * as theirs.
 */
const SCOPES: { value: BookScope; label: string }[] = [
  { value: "all", label: "All users" },
  { value: "referred", label: "Referred only" },
  { value: "mine", label: "Referred by me" },
];

const SCOPE_BLURB: Record<BookScope, string> = {
  all: "Every user, app-wide.",
  referred: "Every user who came through a code.",
  mine: "The people who signed up with your code.",
};

function ScopePicker({
  value,
  onChange,
}: {
  value: BookScope;
  onChange: (next: BookScope) => void;
}) {
  return (
    <div className="segmented segmented-fill" role="group" aria-label="Book scope">
      {SCOPES.map((s) => (
        <button
          key={s.value}
          type="button"
          className={value === s.value ? "selected" : ""}
          aria-pressed={value === s.value}
          onClick={() => onChange(s.value)}
        >
          {s.label}
        </button>
      ))}
    </div>
  );
}

function ReferralsBody({
  rows,
  refreshing,
  error,
  search,
  ownCode,
  compact,
  onOpen,
}: {
  rows: AdminReferralRow[] | null;
  refreshing: boolean;
  error: unknown;
  search: string;
  ownCode: string | null;
  compact: boolean;
  onOpen: (row: AdminReferralRow) => void;
}) {
  if (error) {
    return (
      <div className="panel-body">
        <ErrorNotice error={error} />
      </div>
    );
  }
  if (rows === null) return <Loading what="referrals" />;

  if (rows.length === 0 && search) {
    // Deliberately not "no such user". Name search only matches once a user has
    // passed KYC, and a search outside the caller's book comes back empty
    // rather than 403, so an empty result says nothing about whether the person
    // exists. The copy must not claim otherwise.
    return (
      <div className="empty">
        <strong>No matches in your referrals</strong>
        <p className="subtle">
          Search covers email, referral code and account ID, and names only once
          a user has passed KYC. Someone in another book will not appear here.
        </p>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="empty">
        <strong>No referrals yet</strong>
        <p className="subtle">
          {ownCode
            ? `Share your code ${ownCode}. Anyone who signs up with it shows up here.`
            : "You do not have a referral code yet. Ask an admin to issue one."}
        </p>
      </div>
    );
  }

  if (compact) {
    return (
      <ul className={refreshing ? "list is-stale" : "list"} aria-busy={refreshing}>
        {rows.map((row) => (
          <li key={row.userId}>
            <ReferralListRow row={row} />
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div className={refreshing ? "table-wrap is-stale" : "table-wrap"}>
      <table aria-busy={refreshing}>
        <thead>
          <tr>
            <th>Email</th>
            <th>Name</th>
            <th>Code</th>
            <th className="num">Signed up</th>
            <th>Invite</th>
            <th>KYC</th>
            <th>Card</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.userId} className="clickable" onClick={() => onOpen(row)}>
              <td className="cell-strong">
                <div className="row">
                  {/*
                    A real link, not a cell inside a clickable <tr>: it carries
                    the same row in router state the row click passes, so both
                    routes land on identical data, and it is the only thing on
                    this page a keyboard could ever have used to open a member.
                    The row click stays as an enhancement over it.
                  */}
                  <Link
                    className="cell-link"
                    to={`/users/${row.userId}`}
                    state={{ row }}
                    aria-label={row.email ?? row.userId}
                    onClick={(event) => event.stopPropagation()}
                  >
                    {orDash(row.email)}
                  </Link>
                  {/* The role, not a generic STAFF mark: staff are listed in the
                      book but excluded from the funnel counters, so this is
                      what explains a table longer than "Signed up", and which
                      role says what they can do. End users get no chip. */}
                  {row.role !== "user" ? <RoleChip role={row.role} /> : null}
                </div>
              </td>
              <td>{orDash(row.name)}</td>
              <td>{orDash(row.code)}</td>
              <td className="num">{formatDate(row.signedUpAt)}</td>
              <td>
                <BoolChip value={row.inviteRedeemed} yes="Redeemed" no="Not yet" />
              </td>
              <td>
                <KycChip status={row.kycStatus} />
              </td>
              <td>
                <BoolChip value={row.cardIssued} yes="Issued" no="No card" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * One member as a phone's list row: who, then where they came from, then how
 * far down the funnel they are. The whole row is the link, so the target is
 * the row rather than the email inside it. The funnel chips carry their own
 * names because there is no column header above them to do it.
 */
function ReferralListRow({ row }: { row: AdminReferralRow }) {
  return (
    <Link className="list-row list-link" to={`/users/${row.userId}`} state={{ row }}>
      <span className="list-main">
        <span className="list-title">
          <span className="list-title-text">{orDash(row.email)}</span>
          {row.role !== "user" ? <RoleChip role={row.role} /> : null}
        </span>
        {/* The name on a line of its own: sharing one with the code and the
            date, a long name wrapped the line and left its separator
            starting the next. */}
        {row.name ? (
          <span className="list-meta">
            <span>{row.name}</span>
          </span>
        ) : null}
        <span className="list-meta">
          {row.code ? <span>Code {row.code}</span> : null}
          <span>Joined {formatDate(row.signedUpAt)}</span>
        </span>
        <span className="list-chips">
          <BoolChip value={row.inviteRedeemed} yes="Invite redeemed" no="No invite yet" />
          <KycChip status={row.kycStatus} prefix="KYC " />
          <BoolChip value={row.cardIssued} yes="Card issued" no="No card" />
        </span>
      </span>
      <ChevronRightIcon />
    </Link>
  );
}
